/**
 * Odd Field Out Props Utility
 */

import {
  FieldEntityProps,
  SelectOptions,
  FieldClassNameInterface,
  OddFieldOutPropsType,
} from '../types';

/**
 * If there's an odd number of fields, extracts the last field for full-width display.
 *
 * This is used for 2-column layouts where having an odd number of fields
 * would leave an empty space. The "odd field out" is rendered full-width
 * in its own row.
 *
 * WARNING: This function mutates the input array by popping the last element,
 * but only when that element is a field type it can actually render. An
 * unrenderable trailing field is left in place so the caller still renders it.
 *
 * @param fields - Array of field entities (mutated only when an odd field is claimed)
 * @param selectOptions - Array of select field options
 * @param dependentSelectOptions - Array of dependent select field options
 * @param selectFieldClassNames - CSS class names for select fields
 * @param textFieldClassNames - CSS class names for text fields
 * @returns Props object for the odd field, or false if no odd field
 *
 * @example
 * const oddFieldProps = getOddFieldOutProps(fields, selectOptions, ...);
 * if (oddFieldProps) {
 *   // Render full-width field
 * }
 */
export const getOddFieldOutProps = (
  fields: FieldEntityProps[],
  selectOptions: SelectOptions[],
  dependentSelectOptions: SelectOptions[],
  selectFieldClassNames: FieldClassNameInterface,
  textFieldClassNames: FieldClassNameInterface,
): OddFieldOutPropsType => {
  // Field types this function is able to return renderable props for. Anything
  // else must be left in the array for the caller to render normally.
  const RENDERABLE_TYPES = ['SELECT', 'DEPENDENT_SELECT', 'TEXTAREA', 'TEXT'];

  const isOddFields = fields.length % 2 === 1;
  const candidate = isOddFields ? fields[fields.length - 1] : undefined;

  if (!candidate || !RENDERABLE_TYPES.includes(candidate.fieldType)) return false;

  // Only claim the field once we know it can be rendered. Popping first meant an
  // unsupported type (PHONE, for example) was removed from the caller's array and
  // then dropped by the default branch below, disappearing from the form entirely
  // while still being required by the validation schema.
  const oddFieldOut = fields.pop() as FieldEntityProps;

  const fullWidthSelectClass: FieldClassNameInterface = {
    mainField: `${selectFieldClassNames.mainField} fullWidthSelectField`,
    error: selectFieldClassNames.error,
  };

  const fullWidthTextClass: FieldClassNameInterface = {
    mainField: `${textFieldClassNames.mainField} fullWidthSelectField`,
    error: textFieldClassNames.error,
  };

  switch (oddFieldOut.fieldType) {
    case 'SELECT':
      return {
        type: oddFieldOut.fieldType,
        placeholder: `${oddFieldOut.name}*`,
        field: oddFieldOut,
        selectOptions,
        selectFieldClassNames: fullWidthSelectClass,
        isTouchedAndError: false,
      };
    case 'DEPENDENT_SELECT':
      return {
        type: oddFieldOut.fieldType,
        placeholder: `${oddFieldOut.name}*`,
        field: oddFieldOut,
        dependentSelectOptions,
        selectFieldClassNames: fullWidthSelectClass,
        isTouchedAndError: false,
      };
    case 'TEXTAREA':
    case 'TEXT':
      return {
        type: oddFieldOut.fieldType,
        field: oddFieldOut,
        placeholder: `${oddFieldOut.name}*`,
        textFieldClassNames: fullWidthTextClass,
        isTouchedAndError: false,
      };
    default:
      return false;
  }
};
